import { FC } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@store/auth/AuthProvider'
import { Button } from '@components/ui/button'
import { LogOut, User, Menu, PanelLeft, Search } from 'lucide-react'
import { GlobalSearch } from '@components/shared/GlobalSearch'

const COFARTE_LOGO_URL = '/logo.png'

interface HeaderProps {
  onToggleSidebar: () => void
  onToggleCollapse: () => void
  collapsed: boolean
}

export const Header: FC<HeaderProps> = ({ onToggleSidebar, onToggleCollapse, collapsed }) => {
  const { user, logout } = useAuth()

  return (
    <>
      <header className="sticky top-0 z-50 h-16 bg-white border-b border-gray-200 px-4 lg:px-6 flex items-center justify-between shadow-sm">
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleSidebar}
          className="lg:hidden p-2 -ml-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          aria-label="Abrir menú"
        >
          <Menu className="h-5 w-5" />
        </button>
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex p-2 -ml-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
          title={collapsed ? "Expandir menú" : "Colapsar menú"}
        >
          <PanelLeft className={cn("h-5 w-5 transition-transform duration-300", collapsed && "rotate-180")} />
        </button>
        <Link to="/" className="flex items-center gap-2">
          <img
            src={COFARTE_LOGO_URL}
            alt="COFARTE"
            className="h-7 lg:h-8 w-auto"
            style={{ maxWidth: '100px', objectFit: 'contain' }}
          />
        </Link>
      </div>

      <div className="flex items-center gap-2 lg:gap-4">
        <button
          onClick={() => window.dispatchEvent(new Event('open-global-search'))}
          className="hidden md:flex items-center gap-2 h-9 px-3 text-sm text-gray-400 bg-gray-50 border border-gray-200 rounded-lg hover:border-cofarte-purple/30 hover:bg-cofarte-purple/5 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <Search className="h-4 w-4" />
          <span className="flex-1 text-left">Buscar...</span>
          <kbd className="hidden lg:inline-flex items-center h-5 px-1.5 rounded bg-gray-100 border border-gray-200 font-mono text-[10px] text-gray-400">Ctrl+K</kbd>
        </button>
        <button
          onClick={() => window.dispatchEvent(new Event('open-global-search'))}
          className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          aria-label="Buscar"
        >
          <Search className="h-5 w-5" />
        </button>
        <CartIcon />
        <NotificationBell />
        <ChangesBell />
        {user && (
          <>
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200">
              <User className="h-4 w-4 text-cofarte-purple" />
              <span className="font-medium max-w-[120px] truncate">{user.name1} {user.name2}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-gray-500 hover:text-cofarte-purple hover:bg-cofarte-purple/10"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </>
        )}
      </div>
    </header>

    <GlobalSearch />
    </>
  )
}

import { cn } from '@utils/index'
import { CartIcon } from '@components/shared/CartIcon'
import { NotificationBell } from '@components/shared/NotificationBell'
import { ChangesBell } from '@components/shared/ChangesBell'
